import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ArrowRight, 
  Home,
  MapPin,
  LogIn,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RoofBidderHeader = ({children}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const { user, signOut } = useAuth();

  const handleGetQuote = () => {
    navigate('/get-quote');
  };

  const handleCompareBids = () => {
    navigate('/compare-bids');
  };

  const handleOrders = () => {
    navigate('/orders');
  };

  const handleHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={handleHome}>
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="ml-3 text-2xl font-bold text-gray-900">RoofBidder</span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={handleHome}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Home
              </button>
              <button
                onClick={handleGetQuote}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Get Quote
              </button>
              
              <button
                onClick={handleCompareBids}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Compare Bids
              </button>
              <button
                onClick={handleOrders}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Orders
              </button>
            </nav>

            {/* Auth / CTA */}
            <div className="hidden md:flex items-center space-x-4">
              {!user ? (
                <button
                  data-testid="header-sign-in-button"
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 rounded-xl font-medium text-gray-700 hover:text-blue-600 transition-colors flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4" /> Sign In
                </button>
              ) : (
                <>
                  <div className="text-sm text-gray-600" data-testid="header-user-email">{user.email}</div>
                  <button
                    data-testid="header-sign-out-button"
                    onClick={async () => { await signOut(); }}
                    className="px-4 py-2 rounded-xl font-medium text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              )}
              <button
                onClick={handleGetQuote}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center space-x-2"
              >
                <MapPin className="w-4 h-4" />
                <span>Get Free Quote</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-blue-600 transition-colors"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="space-y-4">
                <button
                  onClick={handleHome}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
                >
                  Home
                </button>
                <button
                  onClick={handleCompareBids}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
                >
                  Compare Bids
                </button>
                <button
                  onClick={handleOrders}
                  className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
                >
                  Orders
                </button>
                {!user ? (
                  <button
                    data-testid="mobile-sign-in-button"
                    onClick={() => navigate('/login')}
                    className="block w-full text-left text-gray-700 hover:text-blue-600 font-medium py-2"
                  >
                    Sign In
                  </button>
                ) : (
                  <button
                    data-testid="mobile-sign-out-button"
                    onClick={async () => { await signOut(); }}
                    className="block w-full text-left text-gray-700 hover:text-red-600 font-medium py-2"
                  >
                    Sign Out
                  </button>
                )}
                <button
                  onClick={handleGetQuote}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center space-x-2 mt-4"
                >
                  <MapPin className="w-4 h-4" />
                  <span>Get Free Quote</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Auth Modal replaced by full pages. Buttons now navigate to /login and /signup. */}
    </div>
  );
};

export default RoofBidderHeader;
